import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { addEntities, removeEntity, updateEntity, withEntities } from '@ngrx/signals/entities';
import { catchError, EMPTY, map, Observable, of, switchMap } from 'rxjs';

import { isEmpty } from '@celum/core';
import { Specification, SpecificationList, TenantSpecificationAssignment } from '@celum/formats/domain';

import { ASSIGNMENTS_SERVICE, AssignmentsService } from '../../services/assignments.service';
import { NotificationService } from '../../services/notification.service';

type AssignedSpecificationsState = {
  loading: boolean;
  updating: boolean;
  pagination: {
    totalCount: number;
    page: number;
    size: number;
    hasNext: boolean;
  };
};

const initialState: AssignedSpecificationsState = {
  loading: true,
  updating: false,
  pagination: {
    totalCount: 0,
    page: 0,
    size: 20,
    hasNext: false
  }
};

export const AssignedSpecificationsStore = signalStore(
  withEntities<Specification>(),
  withState(initialState),
  withMethods((store, assignmentsService = inject(ASSIGNMENTS_SERVICE), notificationService = inject(NotificationService)) => {
    function loadAssignmentsBatch(): void {
      patchState(store, { loading: true });

      const { page, size } = store.pagination;
      getAssignmentsBatch(page(), size(), assignmentsService, notificationService).subscribe(({ content, hasNext, totalElements }) => {
        patchState(store, addEntities(content));
        patchState(store, {
          loading: false,
          pagination: {
            ...store.pagination(),
            totalCount: totalElements,
            hasNext
          }
        });
      });
    }

    function loadNextAssignmentsBatch(): void {
      const { page } = store.pagination();

      patchState(store, { pagination: { ...store.pagination(), page: page + 1 } });
      loadAssignmentsBatch();
    }

    function updateBatchSize(size: number): void {
      patchState(store, { pagination: { ...store.pagination(), size } });
    }

    function assignSpecifications(specs: Specification[]): void {
      if (isEmpty(specs)) {
        return;
      }

      patchState(store, { loading: true });

      const specIds = specs.map(spec => spec.id);
      assignmentsService.assignSpecifications(specIds).subscribe({
        next: tenantSpecificationAssignment => {
          const specsWithAssignment: Specification[] = specs.map(spec => ({
            ...spec,
            assignmentId: tenantSpecificationAssignment.find(assignment => assignment.specificationId === spec.id)?.id
          }));

          patchState(store, addEntities(specsWithAssignment));
          patchState(store, { loading: false });
        },
        error: err => {
          console.error('AssignedSpecificationsStore: error on assigning specifications', err.error);

          notificationService.error(err?.error?.errorMessage);
          patchState(store, { loading: false });
        }
      });
    }

    function unassignSpecification(specId: number): void {
      const spec = store.entityMap()[specId];

      assignmentsService.unassignSpecification(spec.assignmentId).subscribe({
        next: () => patchState(store, removeEntity(specId)),
        error: err => {
          console.error('AssignedSpecificationsStore: error on unassigning specification', err.error);

          notificationService.error(err.error?.errorMessage);
          patchState(store, { loading: false });
        }
      });
    }

    function switchResourceType(specId: number): Observable<TenantSpecificationAssignment> {
      const spec = store.entityMap()[specId];

      const useTenantEnvironment = !spec.isTenantEnvironment;
      patchState(store, { updating: true });

      return assignmentsService.patchAssignment(spec.assignmentId, useTenantEnvironment).pipe(
        map(result => {
          patchState(store, updateEntity({ id: specId, changes: { isTenantEnvironment: useTenantEnvironment } }));
          notificationService.info('SPECIFICATION_LIST.SNACKBAR.UPDATE_SUCCESS');
          patchState(store, { updating: false });
          return result;
        }),
        catchError(err => {
          console.error('AssignedSpecificationsStore: error on switching resource type', err.error);
          notificationService.error('SPECIFICATION_LIST.SNACKBAR.UPDATE_ERROR');
          patchState(store, { updating: false });
          return of(null);
        })
      );
    }

    return {
      loadAssignmentsBatch,
      loadNextAssignmentsBatch,
      updateBatchSize,
      assignSpecifications,
      unassignSpecification,
      switchResourceType
    };
  })
);

function getAssignmentsBatch(
  page: number,
  size: number,
  assignmentsService: AssignmentsService,
  notificationService: NotificationService
): Observable<SpecificationList> {
  return assignmentsService.getTenantSpecifications(page, size).pipe(
    switchMap(({ content, hasNext, totalElements }) => {
      const specIds = content.map(spec => spec.id);
      return assignmentsService.getTenantAssignments(specIds).pipe(
        map(({ content: assignments }) => {
          const contentWithAssignmentInfo = content.map(spec => {
            const assignment = assignments.find(tenantAssignment => tenantAssignment.specificationId === spec.id);
            return {
              ...spec,
              assignmentId: assignment?.id,
              isTenantEnvironment: assignment?.isTenantEnvironment
            };
          });
          return { content: contentWithAssignmentInfo, hasNext, totalElements };
        })
      );
    }),
    catchError(error => {
      console.error('AssignedSpecificationsStore: error on loading assignments', error.error);

      notificationService.error('SPECIFICATION_LIST.SNACKBAR.LOAD_ERROR');
      return EMPTY;
    })
  );
}
